import { GLOBAL_ASSETS_SENTINEL_OWNER_ID } from '@cadstrom/cadstrom-shared';

import type { Organization } from '@cadstrom/cadstrom-shared';

/**
 * Returns an organization's display label, preferring `displayName` when set.
 *
 * @param org The organization to label.
 * @returns The organization's display name, or its organizationName as fallback.
 */
export function getOrganizationDisplayName(org: Organization): string {
	return org.displayName ?? org.organizationName;
}

/**
 * Sorts organizations for presentation in the picker. Non-sentinel organizations are sorted
 * alphabetically ascending by display name. The Global sentinel, if present, is pinned to
 * either the first or last position per `globalOptionPosition`.
 *
 * @param organizations Unsorted list of organizations.
 * @param globalOptionPosition Where to pin the Global sentinel — 'first' or 'last'.
 * @returns A new sorted array. The input is not mutated.
 */
export function sortOrganizations(
	organizations: Organization[],
	globalOptionPosition: 'first' | 'last',
): Organization[] {
	const sentinel = organizations.find((org) => org.id === GLOBAL_ASSETS_SENTINEL_OWNER_ID);
	const others = organizations
		.filter((org) => org.id !== GLOBAL_ASSETS_SENTINEL_OWNER_ID)
		.toSorted((a, b) => getOrganizationDisplayName(a).localeCompare(getOrganizationDisplayName(b)));

	if (sentinel === undefined) {
		return others;
	}

	return globalOptionPosition === 'first' ? [sentinel, ...others] : [...others, sentinel];
}
